import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useSpring, animated } from '@react-spring/web';
import './MarkdownRenderer.css';

const MarkdownRenderer = ({ content }) => {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(index);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // Simple DevOps animation component
  const DevOpsAnimation = ({ id }) => {
    const props = useSpring({
      from: { opacity: 0, transform: 'translateX(-50px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
      config: { duration: 1000 },
      loop: { reverse: true },
    });

    return (
      <animated.div style={props} className="devops-animation">
        <svg width="200" height="100">
          <rect x="10" y="10" width="50" height="30" fill="#0078D4" />
          <text x="15" y="30" fill="white">Build</text>
          <rect x="80" y="10" width="50" height="30" fill="#0078D4" />
          <text x="85" y="30" fill="white">Test</text>
          <rect x="150" y="10" width="50" height="30" fill="#0078D4" />
          <text x="155" y="30" fill="white">Deploy</text>
          <path d="M60 25 H80 M130 25 H150" stroke="black" strokeWidth="2" />
        </svg>
      </animated.div>
    );
  };

  return (
    <ReactMarkdown
      components={{
        // Render headings with id attributes so internal anchors (e.g. #bridge-networking)
        // work in the React app. We slugify the heading text to generate the id.
        h1({ node, children, ...props }) {
          const text = String(children).replace(/\s+/g, ' ').trim();
          const slug = text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
          return (
            <h1 id={slug} {...props}>
              {children}
            </h1>
          );
        },
        h2({ node, children, ...props }) {
          const text = String(children).replace(/\s+/g, ' ').trim();
          const slug = text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
          return (
            <h2 id={slug} {...props}>
              {children}
            </h2>
          );
        },
        h3({ node, children, ...props }) {
          const text = String(children).replace(/\s+/g, ' ').trim();
          const slug = text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
          return (
            <h3 id={slug} {...props}>
              {children}
            </h3>
          );
        },
        h4({ node, children, ...props }) {
          const text = String(children).replace(/\s+/g, ' ').trim();
          const slug = text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
          return (
            <h4 id={slug} {...props}>
              {children}
            </h4>
          );
        },
        h5({ node, children, ...props }) {
          const text = String(children).replace(/\s+/g, ' ').trim();
          const slug = text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
          return (
            <h5 id={slug} {...props}>
              {children}
            </h5>
          );
        },
        h6({ node, children, ...props }) {
          const text = String(children).replace(/\s+/g, ' ').trim();
          const slug = text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
          return (
            <h6 id={slug} {...props}>
              {children}
            </h6>
          );
        },
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const index = node.position?.start?.line || 0;

          return !inline && match ? (
            <div className="code-block">
              <button
                className="copy-button"
                onClick={() => copyToClipboard(String(children).replace(/\n$/, ''), index)}
              >
                {copied === index ? 'Copied!' : 'Copy'}
              </button>
              <SyntaxHighlighter
                style={vscDarkPlus}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            </div>
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
        img({ node, ...props }) {
          const { alt, src, width, height, ...rest } = props;
          // Ensure an alt attribute is always present to satisfy eslint rule
          // Use empty string for decorative images.
          if (alt === 'youtube') {
            return (
              <div className="youtube-embed">
                <iframe
                  width="560"
                  height="315"
                  src={`https://www.youtube.com/embed/${src}`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            );
          } else if (alt === 'devops-animation') {
            return <DevOpsAnimation id={src} />;
          }

          return (
            <img
              src={src}
              alt={typeof alt === 'string' ? alt : ''}
              loading="lazy"
              style={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                marginTop: '1.5rem',
                marginBottom: '1.5rem',
                ...((width || height) ? {} : { objectFit: 'cover' })
              }}
              width={width || undefined}
              height={height || undefined}
              {...rest}
            />
          );
        },
        p({ node, children }) {
          if (node.children[0] && node.children[0].tagName === 'img') {
            const image = node.children[0];
            if (image.properties && (image.properties.alt === 'youtube' || image.properties.alt === 'devops-animation')) {
              return <>{children}</>;
            }
          }
          return <p className="mb-4 leading-relaxed text-gray-700">{children}</p>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;